import React, { useState } from 'react';
import { WEB_SEARCH_PROVIDERS } from '../utils/webSearchProviders';
import { runWebSearchTest } from '../utils/api';
import { WebSearchResultItem, getItemUrl } from '../components/WebSearchSourcesList';

function parseQueriesInput(raw) {
  return String(raw || '')
    .split(',')
    .map((q) => q.replace(/\s+/g, ' ').trim())
    .filter(Boolean);
}

function AiWebSearchTestPanel({ setMessage }) {
  const [provider, setProvider] = useState('serper');
  const [queriesText, setQueriesText] = useState('Sony news, Microsoft earnings');
  const [limit, setLimit] = useState(5);
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState(null);

  const handleRun = async () => {
    const queries = parseQueriesInput(queriesText);
    if (!queries.length) {
      setMessage({ type: 'error', text: 'Enter at least one search query (comma-separated).' });
      return;
    }

    try {
      setRunning(true);
      setMessage(null);
      setResults(null);
      const payload = await runWebSearchTest({
        provider,
        queries: queriesText,
        limit: Number(limit) || 5
      });
      setResults(payload);
      const failed = (payload.results || []).filter((r) => !r.success).length;
      const ok = (payload.results || []).filter((r) => r.success).length;
      setMessage({
        type: failed > 0 && ok === 0 ? 'error' : 'success',
        text: `Web search test finished: ${ok} succeeded, ${failed} failed (provider: ${payload.provider_used || provider}).`
      });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Web search test failed', details: err.details || null });
      setResults(null);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="web-search-test-panel">
      <p className="panel-intro">
        Run live searches using <strong>your</strong> Web Search settings from User Settings
        (API keys or &quot;I feel lucky!&quot; scrape mode).
        Enter multiple queries in one field, separated by commas.
      </p>

      <div className="web-search-test-form">
        <div className="form-group">
          <label htmlFor="wsProvider">Search API</label>
          <select
            id="wsProvider"
            className="form-control"
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
            disabled={running}
          >
            {WEB_SEARCH_PROVIDERS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.keyName})
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="wsQueries">Queries (comma-separated)</label>
          <textarea
            id="wsQueries"
            className="form-control"
            rows={3}
            value={queriesText}
            onChange={(e) => setQueriesText(e.target.value)}
            placeholder="recent news about Sony, Apple AI announcements"
            disabled={running}
          />
          <small className="form-help">
            Example: <code>Sony gaming news, PlayStation 2026</code> runs two searches.
          </small>
        </div>

        <div className="form-group form-group-inline">
          <label htmlFor="wsLimit">Results per query</label>
          <input
            id="wsLimit"
            type="number"
            min={1}
            max={20}
            className="form-control"
            value={limit}
            onChange={(e) => setLimit(e.target.value)}
            disabled={running}
          />
        </div>

        <button type="button" className="btn btn-primary" onClick={handleRun} disabled={running}>
          {running ? (
            <>
              <i className="fas fa-spinner fa-spin" /> Running…
            </>
          ) : (
            <>
              <i className="fas fa-search" /> Run searches
            </>
          )}
        </button>
      </div>

      {results?.results?.length > 0 && (
        <div className="web-search-test-results">
          <h4>
            Results
            {results.provider_used && (
              <span className="provider-used"> — used {results.provider_used}</span>
            )}
          </h4>
          {results.results.map((row, idx) => (
            <div
              key={`${row.query}-${idx}`}
              className={`ws-result-card ${row.success ? 'success' : 'failed'}`}
            >
              <div className="ws-result-header">
                <strong>{row.query}</strong>
                <span className={`ws-result-badge ${row.success ? 'ok' : 'fail'}`}>
                  {row.success ? 'OK' : 'FAILED'}
                </span>
              </div>
              {!row.success && (
                <div className="ws-result-error">
                  <div>{row.error || 'Search failed'}</div>
                  {row.error_details && (
                    <pre className="ws-result-error-details">
                      {JSON.stringify(row.error_details, null, 2)}
                    </pre>
                  )}
                </div>
              )}
              {row.success && (
                <>
                  <div className="ws-result-meta">
                    {row.result_count ?? 0} result(s)
                    {row.duration_ms != null ? ` · ${row.duration_ms} ms` : ''}
                  </div>
                  {Array.isArray(row.items) && row.items.length > 0 ? (
                    <ul className="ws-result-items">
                      {row.items.map((item, i) => (
                        <WebSearchResultItem key={`${getItemUrl(item) || 'item'}-${i}`} item={item} />
                      ))}
                    </ul>
                  ) : (
                    <p className="ws-no-items">No organic results returned.</p>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default AiWebSearchTestPanel;
